import React, { useState, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import MonacoEditor from "react-monaco-editor";
import * as monaco from "monaco-editor";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button, Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPaperPlane, faCheck, faXmark, faGraduationCap, faDownload } from "@fortawesome/free-solid-svg-icons";
import ListenButton from "../LessonAudio/ListenButton";
import MyAppIdeaCard from "./MyAppIdeaCard.js";
import PaneResizer, { clampSize } from "./PaneResizer.js";
import { LiveStatus, useStatus, useOnChange, refocusIfLost } from "./workspaceA11y.js";
import "./PromptLabLayout.css";
import { AiFirstUseNotice, AiMistakesNote, AiGradedNote } from "../AiNotice/AiNotice";

/**
 * Three-panel layout for the Claude Course Prompt Lab.
 *
 *   +------------+‖--------------------+‖-----------------+
 *   |  Lesson    |‖   Prompt Lab       |‖   Code Editor   |
 *   |  markdown  |‖   (chat w/ Claude) |‖+===============+|
 *   |            |‖                    |‖   Console       ||
 *   +------------+‖--------------------+‖-----------------+
 *
 * For rubric-graded lessons the right column shows the rubric + the judge's
 * feedback instead of editor/console.
 *
 * `captureProject` (the app-idea lesson — V2101 / V3101 / V4101) rearranges it,
 * because there the graded work is a FORM rather than a conversation:
 *
 *   +------------+‖--------------------+‖-----------------+
 *   |  Lesson    |‖   My app idea      |‖   Rubric        |
 *   |  markdown  |‖   (the form —      |‖+===============+|
 *   |            |‖    what's graded)  |‖   Prompt Lab    ||
 *   +------------+‖--------------------+‖-----------------+
 *
 * The middle column is always where the student works, so the card takes it and
 * the chat drops under the rubric as the optional thinking partner it is on that
 * lesson — the same shape as the canvas and checklist layouts.
 *
 * The panels are separated by draggable split-pane dividers (`.pl-resizer`):
 * two vertical ones (lesson↔middle, middle↔right) and — for code lessons and the
 * app-idea lesson — one horizontal one in the right column. Each divider IS the
 * border and is driven by pointer events, so the whole border is the drag target.
 * They also take focus and move with the arrow keys (see PaneResizer).
 */
const PromptLabLayout = ({
    // lesson data
    definition,
    lessonTitle,
    type,
    grading,
    // chat
    messages,
    input,
    setInput,
    onSend,
    isThinking,
    // code lessons
    editorCode,
    setEditorCode,
    language,
    consoleOutput,
    onRun,
    isRunning,
    onSubmit,
    testResults,
    // concept lessons
    judgeResult,
    isJudging,
    // shared
    allPassed,
    // The app-idea lesson: the "My App Idea" card takes the middle column and IS
    // the graded exercise (see appIdeaLesson.js), so the chat moves under the
    // rubric and stops being what the rubric reads.
    captureProject,
    // Build-spec lessons (V2401 / V3401) only — see buildSpecLesson.js.
    // `rubricSlots` runs parallel to definition.rubric and marks the lines the
    // student already satisfied in Module 1; the download is their reward for a
    // perfect score and is absent everywhere else.
    rubricSlots,
    downloadReady,
    // Keep the chat and Submit usable after a pass, for lessons whose reward sits
    // above the pass mark. See the note where the hub computes it.
    chatStaysOpen,
    isPreparingDownload,
    onDownloadApp,
}) => {
    const { t } = useTranslation();
    const [leftWidth, setLeftWidth] = useState(400);
    const [middleWidth, setMiddleWidth] = useState(500);
    // Height of the right column's TOP block — the editor on code lessons, the
    // rubric on the app-idea lesson. Both sit above a horizontal divider.
    const [rightTopHeight, setRightTopHeight] = useState(400);
    const containerRef = useRef(null);
    const rightColRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const submitRef = useRef(null);
    const judgeResultRef = useRef(null);
    const sendHintId = useId();

    // Initial split sizes — left/middle thirds, right fills the rest.
    useEffect(() => {
        const setInitial = () => {
            const totalWidth = containerRef.current
                ? containerRef.current.clientWidth
                : window.innerWidth;
            const each = Math.floor(totalWidth / 3);
            setLeftWidth(each);
            setMiddleWidth(each);
            setRightTopHeight(Math.floor(window.innerHeight * 0.45));
        };

        setInitial();
        window.addEventListener("resize", setInitial);
        return () => window.removeEventListener("resize", setInitial);
    }, []);

    // Monaco custom theme — match the existing CodeExecutor look.
    useEffect(() => {
        try {
            monaco.editor.defineTheme("promptLabTheme", {
                base: "vs-dark",
                inherit: true,
                rules: [],
                colors: {
                    "editor.background": "#272727",
                },
            });
        } catch (e) {
            // monaco may not be ready in some test envs; ignore
        }
    }, []);

    // Auto-scroll chat to bottom on new messages.
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages.length, isThinking]);

    // Auto-grow the prompt input like Claude/ChatGPT: reset to one line, then
    // expand to fit the content up to a cap (after which it scrolls internally).
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
    }, [input]);

    // Each divider's limits for a given container size, shared by dragging and
    // the keyboard so both stop in the same place.
    const leftLimits = (width) => ({ min: 240, max: width - middleWidth - 12 - 220 });
    const middleLimits = (width) => ({ min: 260, max: width - leftWidth - 12 - 220 });
    const rightTopLimits = (height) => ({ min: 120, max: height - 140 });
    const containerWidth = () => containerRef.current?.clientWidth || window.innerWidth;
    const rightColHeight = () => rightColRef.current?.clientHeight || window.innerHeight;

    // Drag the lesson ↔ chat border → resize the lesson column.
    const startLeftResize = (e) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = leftLimits(rect.width);
            setLeftWidth(clampSize(ev.clientX - rect.left, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-col");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-col");
    };

    // Drag the chat ↔ right border → resize the chat column (right fills rest).
    const startMiddleResize = (e) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = middleLimits(rect.width);
            setMiddleWidth(clampSize(ev.clientX - rect.left - leftWidth - 6, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-col");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-col");
    };

    // Drag the horizontal border in the right column → resize its top block
    // (editor ↔ console, or rubric ↔ chat on the app-idea lesson).
    const startRightTopResize = (e) => {
        e.preventDefault();
        const col = rightColRef.current;
        if (!col) return;
        const rect = col.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = rightTopLimits(rect.height);
            setRightTopHeight(clampSize(ev.clientY - rect.top, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-row");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-row");
    };

    const handleEditorChange = (value) => setEditorCode(value);

    const editorLabel = t("promptLab.codeEditor", { lang: language || "code" });

    const editorOptions = {
        selectOnLineNumbers: true,
        readOnly: false,
        fontSize: 14,
        wordWrap: "on",
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        automaticLayout: true,
        ariaLabel: editorLabel,
        accessibilitySupport: "auto",
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            if (!isThinking) onSend();
        }
    };

    // Send keeps focus in the composer. The button disables itself once the box
    // is empty, which would otherwise drop focus to <body>.
    const sendFromButton = () => {
        onSend();
        textareaRef.current?.focus();
    };

    // Rubric-graded lessons show the rubric/judge panel; code lessons show the
    // editor + console + tests. `grading` is authoritative; fall back to `type`
    // for legacy (CC) lessons that don't declare a grading model.
    const isConcept = grading ? grading === "rubric" : type === "concept";

    // Exercise "Passed" badge — code (test-graded) lessons only; concept lessons
    // report their result in the rubric panel instead. `allPassed` also covers a
    // lesson restored as already complete (before any test run this session).
    const hasTestRun = Array.isArray(testResults) && testResults.length > 0;
    const exercisePassed = !isConcept && (allPassed || (hasTestRun && testResults.every((r) => r.passed)));

    const dragTitle = t("promptLab.dragToResize", "Drag to resize");

    // Passing normally ends a lesson and closes its chat. `chatStaysOpen` is the
    // exception for lessons that still have something to earn above the pass mark.
    // On the app-idea lesson the chat never graded anything, so locking it would
    // only take away the one place a student can ask "what now?" — it stays open.
    const chatLocked = allPassed && !chatStaysOpen && !captureProject;

    // The app-idea lesson's rearrangement (see the diagram above). Capture lessons
    // are rubric-graded by definition, so this always implies `isConcept`.
    const ideaMode = !!captureProject;

    // Short spoken summaries. The chat log reads the reply itself; this says a
    // reply is coming, how a run went and how the grade came out.
    const [status, announce] = useStatus();

    useOnChange(!!isThinking, (thinking) => {
        if (thinking) return announce(t("promptLab.statusReplying"));
        announce(t("promptLab.statusReplied"));
        refocusIfLost(textareaRef.current);
    });

    useOnChange(!!isRunning, (running) => {
        if (running) return announce(t("promptLab.running"));
        const total = Array.isArray(testResults) ? testResults.length : 0;
        announce(
            total > 0
                ? t("promptLab.statusRunTests", { passed: testResults.filter((r) => r.passed).length, total })
                : t("promptLab.statusRunFinished")
        );
    });

    // Submit disables itself while grading, so focus goes back to it (or to the
    // chat) when grading ends. A pass that locks the chat leaves both disabled,
    // so focus goes to the result. Not on the app-idea lesson, whose chat is optional.
    useOnChange(!!isJudging, (judging) => {
        if (judging) announce(t("promptLab.grading"));
        else if (!ideaMode) {
            refocusIfLost(submitRef.current) ||
                refocusIfLost(textareaRef.current) ||
                refocusIfLost(judgeResultRef.current);
        }
    });

    // Only a grade the AI produced reads as "Graded"; the hub's own notes (no
    // conversation yet, judge unreachable, ...) are read as they are.
    useOnChange(judgeResult, (result) => {
        if (!result) return;
        if (!result.aiGraded) return announce(result.feedback);
        announce(t(result.passed ? "promptLab.statusGradedPassed" : "promptLab.statusGradedNotYet"));
    });

    // One chat panel, two homes. On most lessons it IS the exercise and owns the
    // middle column, with Submit for grading under it. On the app-idea lesson it is
    // an optional helper below the rubric — labelled as such, and carrying no
    // Submit, because pressing it there would suggest the conversation is what gets
    // read. Written once so the two can't drift apart.
    const renderChatCard = ({ optional }) => (
        <Card className="prompt-lab-card">
            <CardHeader className="prompt-lab-card-header">
                <CardTitle tag="h5" className="mb-0">
                    {optional ? t("promptLab.promptLabShort") : t("promptLab.promptLabTitle")}
                </CardTitle>
                {optional && (
                    <span className="prompt-lab-chat-sub">{t("promptLab.askAnythingOptional")}</span>
                )}
            </CardHeader>
            {/* A log, so new messages are read out. The "Thinking…" bubble is
                hidden from it: the status line already says Claude is replying. */}
            <CardBody className="prompt-lab-chat-messages" role="log" aria-live="polite" aria-relevant="additions">
                {optional && messages.length === 0 && (
                    <div className="prompt-lab-chat-empty">{t("promptLab.ideaChatEmpty")}</div>
                )}
                {messages.map((m, i) => (
                    <div key={i} className={`prompt-lab-msg prompt-lab-msg--${m.role}`}>
                        <div className="prompt-lab-msg-role">{m.role === "user" ? t("promptLab.you") : "Claude"}</div>
                        <div className="prompt-lab-msg-content">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ""}</ReactMarkdown>
                        </div>
                    </div>
                ))}
                {isThinking && (
                    <div className="prompt-lab-msg prompt-lab-msg--assistant prompt-lab-msg--thinking" aria-hidden="true">
                        <div className="prompt-lab-msg-role">Claude</div>
                        <div className="prompt-lab-msg-content">{t("promptLab.thinking")}</div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </CardBody>
            <div className="prompt-lab-chat-input">
                <AiFirstUseNotice />
                <div className="pl-chat-inputbox">
                    {/* Read-only, not disabled, while Claude replies: a disabled
                        field drops keyboard focus to <body>. */}
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={optional ? t("promptLab.askLessonPlaceholder") : t("promptLab.promptPlaceholder")}
                        rows={1}
                        aria-label={t("promptLab.messageLabel")}
                        aria-describedby={sendHintId}
                        readOnly={isThinking}
                        aria-busy={!!isThinking}
                        disabled={chatLocked}
                    />
                    <Button
                        type="button"
                        size="sm"
                        className="pl-chat-send"
                        onClick={sendFromButton}
                        disabled={isThinking || !input?.trim() || chatLocked}
                        aria-label={t("promptLab.send")}
                        title={t("promptLab.send")}
                    >
                        <FontAwesomeIcon icon={faPaperPlane} />
                    </Button>
                </div>
                <div id={sendHintId} className="pl-chat-hint">{t("promptLab.sendShortcutHint")}</div>
                <AiMistakesNote />
                {/* Submit for grading is for rubric (conversation) lessons only;
                    code lessons are graded inline by the editor's Run button, and
                    the app-idea lesson submits from its card. */}
                {!optional && isConcept && (
                    <div className="prompt-lab-chat-actions">
                        <Button
                            type="button"
                            size="sm"
                            className="pl-chat-submit"
                            innerRef={submitRef}
                            onClick={onSubmit}
                            disabled={isThinking || isJudging || chatLocked}
                        >
                            <FontAwesomeIcon icon={faGraduationCap} />
                            {t("promptLab.submitForGrading")}
                        </Button>
                    </div>
                )}
            </div>
        </Card>
    );

    // Did Module 1 actually settle anything? Picks the rubric intro below, so a
    // student with an empty checklist isn't told about ticks they don't have.
    const anyCarried = !!rubricSlots?.some((s) => s?.carried);

    // Rubric + judge feedback. Fills the right column on most rubric lessons; on
    // the app-idea lesson it takes the top half and the chat takes the bottom.
    const rubricCard = (
        <Card className="prompt-lab-card">
            <CardHeader className="prompt-lab-card-header">
                <CardTitle tag="h5" className="mb-0">{t("promptLab.rubric")}</CardTitle>
            </CardHeader>
            <CardBody className="prompt-lab-rubric-body">
                <p className="prompt-lab-rubric-intro">
                    {/* Four framings, because the student is being graded on
                        different things: their conversation, the card they filled
                        in, or — on the build-spec lesson — a checklist where only
                        the last line asks for the spec written out in one piece.
                        That last one splits again on whether anything actually
                        arrived ticked: most students carry nothing over, and the
                        "ticked lines are already settled" sentence describes a
                        state they are not in. */}
                    {rubricSlots
                        ? t(anyCarried ? "buildSpec.rubricIntro" : "buildSpec.rubricIntroFresh")
                        : ideaMode
                            ? t("promptLab.ideaRubricIntro")
                            : t("promptLab.rubricIntro")}
                </p>
                <ol className="prompt-lab-rubric-list">
                    {(definition?.rubric || []).map((c, i) => {
                        const result = judgeResult?.perCriterion?.[i];
                        // Settled back in Module 1. Shown ticked before the
                        // student does anything, so the checklist reads as
                        // "here's what's left" rather than a wall of work
                        // they have already done once.
                        const carried = !result && !!rubricSlots?.[i]?.carried;
                        const state = result ? (result.passed ? "passed" : "failed") : carried ? "carried" : "";
                        return (
                            <li key={i} className={state}>
                                {(result || carried) && (
                                    <FontAwesomeIcon icon={result && !result.passed ? faXmark : faCheck} className="mr-1" />
                                )}
                                {c}
                                {carried && (
                                    <span className="prompt-lab-rubric-carried">{t("buildSpec.carriedOver")}</span>
                                )}
                                {result?.reason && <div className="prompt-lab-rubric-reason">{result.reason}</div>}
                            </li>
                        );
                    })}
                </ol>

                {isJudging && <div className="prompt-lab-judging">{t("promptLab.grading")}</div>}

                {judgeResult && (
                    <div
                        ref={judgeResultRef}
                        tabIndex={-1}
                        className={`prompt-lab-judge-result ${judgeResult.passed ? "passed" : "failed"}`}
                    >
                        <strong>{judgeResult.passed ? t("promptLab.passed") : t("promptLab.notYet")}</strong>
                        {typeof judgeResult.score === "number" && <span> — {judgeResult.score}%</span>}
                        <div>{judgeResult.feedback}</div>
                        {judgeResult.aiGraded && <AiGradedNote />}
                    </div>
                )}

                {/* Passed, but the reward is still out of reach. Without
                    this the student sees a green "Passed" and no reason
                    to keep talking — the download would just never
                    appear and they'd never know it existed. */}
                {chatStaysOpen && allPassed && !downloadReady && (
                    <div className="prompt-lab-spec-nudge">
                        {t("buildSpec.almostThere")}
                    </div>
                )}

                {downloadReady && (
                    <div className="prompt-lab-spec-download">
                        <div className="prompt-lab-spec-download-lead">
                            {t("buildSpec.downloadLead")}
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            color="success"
                            onClick={onDownloadApp}
                            disabled={isPreparingDownload}
                        >
                            <FontAwesomeIcon icon={faDownload} /> &nbsp;
                            {isPreparingDownload ? t("buildSpec.preparing") : t("buildSpec.downloadApp")}
                        </Button>
                        <div className="prompt-lab-spec-download-note">
                            {t("buildSpec.downloadNote")}
                        </div>
                    </div>
                )}
            </CardBody>
        </Card>
    );

    return (
        <div ref={containerRef} className="prompt-lab-root">
            {/* LEFT: Lesson */}
            <div
                className="prompt-lab-pane prompt-lab-pane--lesson"
                style={{ width: leftWidth, flex: "0 0 auto" }}
            >
                <div className="prompt-lab-pane-inner">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <CardTitle tag="h5" className="mb-0">
                                {lessonTitle || definition?.title || t("promptLab.lessonFallback")}
                            </CardTitle>
                            <ListenButton text={definition?.lessonMarkdown} lessonID={definition?.lessonID} />
                        </CardHeader>
                        <CardBody className="prompt-lab-lesson-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {definition?.lessonMarkdown || ""}
                            </ReactMarkdown>

                            {definition?.exercisePrompt && (
                                <div className={`prompt-lab-exercise${exercisePassed ? " prompt-lab-exercise--passed" : ""}`}>
                                    <div className="prompt-lab-exercise-head">
                                        <strong>{t("promptLab.yourExercise")}</strong>
                                        {exercisePassed && (
                                            <span className="prompt-lab-exercise-badge passed">
                                                <FontAwesomeIcon icon={faCheck} />
                                                {t("promptLab.passed")}
                                            </span>
                                        )}
                                    </div>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {definition.exercisePrompt}
                                    </ReactMarkdown>
                                </div>
                            )}

                        </CardBody>
                    </Card>
                </div>
            </div>

            {/* Draggable border: Lesson ↔ middle column */}
            <PaneResizer
                value={leftWidth}
                {...leftLimits(containerWidth())}
                onResize={setLeftWidth}
                onPointerDown={startLeftResize}
                label={t("promptLab.resizePane", { pane: t("promptLab.lessonFallback") })}
                title={dragTitle}
            />

            {/* MIDDLE: where the student works — the "My app idea" card on the
                app-idea lesson, the chat with Claude everywhere else. */}
            <div
                className={`prompt-lab-pane prompt-lab-pane--${ideaMode ? "idea" : "chat"}`}
                style={{ width: middleWidth, flex: "0 0 auto" }}
            >
                <div className="prompt-lab-pane-inner">
                    {ideaMode ? (
                        <Card className="prompt-lab-card">
                            <CardHeader className="prompt-lab-card-header">
                                <CardTitle tag="h5" className="mb-0">{t("promptLab.myAppIdea.title")}</CardTitle>
                            </CardHeader>
                            <CardBody className="prompt-lab-idea-body">
                                {/* The card carries the lesson's only button: it
                                    saves the idea AND submits it for grading, in that
                                    order, because the judge reads what was saved. */}
                                <MyAppIdeaCard
                                    showHeading={false}
                                    onSubmitForGrading={onSubmit}
                                    isJudging={isJudging}
                                />
                            </CardBody>
                        </Card>
                    ) : (
                        renderChatCard({ optional: false })
                    )}
                </div>
            </div>

            {/* Draggable border: middle ↔ right column */}
            <PaneResizer
                value={middleWidth}
                {...middleLimits(containerWidth())}
                onResize={setMiddleWidth}
                onPointerDown={startMiddleResize}
                label={t("promptLab.resizePane", {
                    pane: ideaMode ? t("promptLab.myAppIdea.title") : t("promptLab.promptLabShort"),
                })}
                title={dragTitle}
            />

            {/* RIGHT: Editor + Console (code), Rubric (concept), or — on the
                app-idea lesson — Rubric above the optional chat. */}
            <div ref={rightColRef} className="prompt-lab-pane prompt-lab-pane--right" style={{ flex: "1 1 auto" }}>
                {isConcept ? (
                    ideaMode ? (
                        <>
                            {/* Rubric (top) */}
                            <div className="prompt-lab-right-top" style={{ height: rightTopHeight, flex: "0 0 auto" }}>
                                {rubricCard}
                            </div>

                            {/* Draggable border: Rubric ↔ Prompt Lab */}
                            <PaneResizer
                                orientation="horizontal"
                                value={rightTopHeight}
                                {...rightTopLimits(rightColHeight())}
                                onResize={setRightTopHeight}
                                onPointerDown={startRightTopResize}
                                label={t("promptLab.resizePane", { pane: t("promptLab.rubric") })}
                                title={dragTitle}
                            />

                            {/* Prompt Lab (bottom) — optional helper, never graded */}
                            <div className="prompt-lab-right-bottom prompt-lab-right-bottom--chat">
                                {renderChatCard({ optional: true })}
                            </div>
                        </>
                    ) : (
                        <div className="prompt-lab-pane-inner">{rubricCard}</div>
                    )
                ) : (
                    <>
                        {/* Editor (top) */}
                        <div className="prompt-lab-right-top" style={{ height: rightTopHeight, flex: "0 0 auto" }}>
                            <Card className="prompt-lab-card">
                                <CardHeader className="prompt-lab-card-header prompt-lab-editor-header">
                                    <span>{editorLabel}</span>
                                    <Button onClick={onRun} size="sm" color="success" disabled={!editorCode}>
                                        <FontAwesomeIcon icon={faPlay} /> &nbsp; {t("promptLab.run")}
                                    </Button>
                                </CardHeader>
                                <div className="pl-editor-hint">{t("promptLab.editorKeyboardHint")}</div>
                                <CardBody className="prompt-lab-editor-body">
                                    <MonacoEditor
                                        language={language || "python"}
                                        theme="vs-dark"
                                        value={editorCode || ""}
                                        onChange={handleEditorChange}
                                        options={editorOptions}
                                    />
                                </CardBody>
                            </Card>
                        </div>

                        {/* Draggable border: Editor ↔ Console */}
                        <PaneResizer
                            orientation="horizontal"
                            value={rightTopHeight}
                            {...rightTopLimits(rightColHeight())}
                            onResize={setRightTopHeight}
                            onPointerDown={startRightTopResize}
                            label={t("promptLab.resizePane", { pane: editorLabel })}
                            title={dragTitle}
                        />

                        {/* Console (bottom) */}
                        <div className="prompt-lab-right-bottom">
                            <Card className="prompt-lab-card">
                                <CardHeader className="prompt-lab-card-header">
                                    <span>{t("promptLab.console")}</span>
                                </CardHeader>
                                <CardBody className="prompt-lab-console">
                                    <pre className="prompt-lab-console-output">
                                        {consoleOutput || t("promptLab.noOutputYet")}
                                    </pre>
                                </CardBody>
                            </Card>
                        </div>
                    </>
                )}
            </div>

            <LiveStatus message={status} />
        </div>
    );
};

export default PromptLabLayout;
